/**
 * Extracts the entities visible on the current page (course, group, stream…)
 * from the URL, so the assistant understands references like "this group"
 * without the user spelling out course/stream/group names.
 *
 * Patterns mirror the routes in src/routes/paths.ts — keep them in sync when
 * adding entity pages.
 */

export type PageContext = {
  path: string;
  entities: Record<string, string>;
};

const ROUTE_ENTITY_PATTERNS: Array<{ pattern: RegExp; keys: string[] }> = [
  { pattern: /^\/courses\/([^/]+)\/lecture\/([^/]+)/, keys: ['course_id', 'lecture_id'] },
  { pattern: /^\/courses\/([^/]+)\/exam\/([^/]+)/, keys: ['course_id', 'exam_id'] },
  { pattern: /^\/courses\/([^/]+)\/survey\/([^/]+)/, keys: ['course_id', 'survey_id'] },
  { pattern: /^\/courses\/([^/]+)/, keys: ['course_id'] },
  { pattern: /^\/students\/(?!import(?:\/|$))([^/]+)/, keys: ['student_id'] },
  { pattern: /^\/assignments\/([^/]+)/, keys: ['assignment_id'] },
  { pattern: /^\/streams\/([^/]+)/, keys: ['stream_id'] },
  { pattern: /^\/groups\/([^/]+)/, keys: ['group_id'] },
  { pattern: /^\/members\/([^/]+)/, keys: ['member_id'] },
  { pattern: /^\/course-collections\/([^/]+)/, keys: ['collection_id'] },
  { pattern: /^\/exam-submissions\/([^/]+)/, keys: ['exam_id'] },
  { pattern: /^\/practice-results\/([^/]+)/, keys: ['lecture_id'] },
  { pattern: /^\/chats\/([^/]+)/, keys: ['chat_id'] },
  { pattern: /^\/live-sessions\/([^/]+)/, keys: ['live_session_id'] },
  { pattern: /^\/integrations\/([^/]+)/, keys: ['provider'] },
];

/** Query params that carry entity/filter context worth passing to the LLM. */
const CONTEXT_SEARCH_PARAMS = new Set([
  'course_id',
  'stream_id',
  'group_id',
  'section_id',
  'lecture_id',
  'student_id',
  'member_id',
  'tariff_id',
  'unit_id',
  'assignment_id',
]);

export const buildPageContext = (): PageContext => {
  const { pathname, search } = window.location;
  const entities: Record<string, string> = {};

  const match = ROUTE_ENTITY_PATTERNS.find(({ pattern }) => pattern.test(pathname));
  if (match) {
    const values = pathname.match(match.pattern);
    match.keys.forEach((key, index) => {
      const value = values?.[index + 1];
      if (value) {
        try {
          entities[key] = decodeURIComponent(value);
        } catch {
          entities[key] = value;
        }
      }
    });
  }

  new URLSearchParams(search).forEach((value, key) => {
    if (CONTEXT_SEARCH_PARAMS.has(key) && value && !entities[key]) {
      entities[key] = value;
    }
  });

  return { path: pathname, entities };
};
